<template>
    <div class="cart-summary">
        <div class="line labels">
            <div class="line-col product">{{ $t("checkout.cart_label_product") }}</div>
            <div class="line-col count">{{ $t("checkout.cart_label_count") }}</div>
            <div class="line-col item-price">{{ $t("checkout.cart_label_item_price") }}</div>
            <div class="line-col line-price">{{ $t("checkout.cart_label_line_price") }}</div>
            <div class="line-col remove">&nbsp;</div>
        </div>

        <!-- Loading state -->
        <div v-if="isLoading" class="text-center py-3">
            <span class="spinner-border spinner-border" role="status" aria-hidden="true"></span>
        </div>

        <!-- Empty cart state -->
        <div v-else-if="!cartData || !cartData.lineItems || cartData.lineItems.length === 0" class="py-3 text-center">
            <p>{{ $t("checkout.cart_empty") }}</p>
        </div>

        <!-- Cart with items state -->
        <template v-else>
            <div class="line item" v-for="lineItem in cartData.lineItems" :key="lineItem.id">
                <div class="line-col product">
                    <div class="thumb">
                        <img v-if="lineItem.type == 'product'" :src="lineItem?.payload?.coverImg ? lineItem?.payload.coverImg : '/images/product-placeholder.jpg'" alt="" />
                        <div v-else class="voucher-icon d-flex align-items-center justify-content-center">
                            <span class="text-muted">{{ $t("checkout.voucher") }}</span>
                        </div>
                    </div>
                    <div v-if="isVoucher(lineItem)" class="meta">
                        <p class="title">{{ lineItem?.label }}</p>
                    </div>
                    <div v-else class="meta">
                        <a v-if="lineItem?.payload?.productUrl" :href="lineItem?.payload.productUrl" class="title">
                            {{ lineItem?.label }}
                            <span v-if="lineItem?.payload?.variant">({{ lineItem?.payload?.variant }})</span>
                        </a>
                        <p v-else class="title">
                            {{ lineItem?.label }}
                            <span v-if="lineItem?.payload?.variant">({{ lineItem?.payload?.variant }})</span>
                        </p>

                        <p v-if="lineItem?.payload?.description" class="description mb-0">{{ lineItem?.payload?.description }}</p>
                        <small v-if="lineItem?.payload?.productVoucherCodeProduct" class="info">{{ $t("checkout.cart_label_voucher") }} {{ formatVoucher(lineItem?.payload.productVoucherCodeProduct) }}</small>
                        <small v-if="showStockStatus(lineItem)" class="warning">{{ $t(`product_detail.${lineItem.payload.stockStatus}_description`) }}</small>
                    </div>
                </div>
                <div class="line-col count">
                    <div v-if="canUpdateQuantity(lineItem)" class="quantity-controls">
                        <button
                            class="btn btn-sm btn-outline-secondary quantity-btn"
                            @click="decrementQuantity(lineItem)"
                            :disabled="isUpdatingQuantity(lineItem.id) || lineItem.quantity <= 1"
                        >
                            <span>-</span>
                        </button>

                        <input
                            type="number"
                            class="form-control form-control-sm quantity-input"
                            :value="lineItem.quantity"
                            min="1"
                            @change="debouncedUpdateQuantity(lineItem.id, $event.target.value)"
                            @focus="$event.target.select()"
                        />

                        <button
                            class="btn btn-sm btn-outline-secondary quantity-btn"
                            @click="incrementQuantity(lineItem)"
                            :disabled="isUpdatingQuantity(lineItem.id)"
                        >
                            <span>+</span>
                        </button>
                    </div>
                    <span class="small" v-else>{{ lineItem.quantity }}</span>
                </div>
                <div class="line-col item-price">
                    <span>&euro;{{ formatNumber(lineItem?.price?.unitPrice) }}</span>
                </div>
                <div class="line-col line-price">
                    <span v-if="!isUpdatingQuantity(lineItem.id)">&euro;{{ formatNumber(lineItem?.price?.totalPrice) }}</span>
                    <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                </div>
                <div class="line-col remove">
                    <button v-if="lineItem.removable" class="btn remove-link" @click="removeLineItem(lineItem.id)" :disabled="isUpdatingQuantity(lineItem.id)">
                        <ph-trash />
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import { PhTrash } from 'phosphor-vue';
import _ from 'lodash';
import { Api } from '../../api/apiClient';
import { EventBus } from '../../api/eventBus.ts';
import formatters from '../../mixins/formatters';
import lineitem from '../../mixins/line-item';

export default {
    name: "CartLineItems",
    mixins: [lineitem, formatters],
    components: {
        PhTrash,
    },
    props: {
        cart: Object,
    },
    data() {
        return {
            api: new Api(),
            internalCart: null,
            updatingQuantityItems: [],
            isLoading: false,
            retryCount: 0,
            maxRetries: 3
        };
    },
    computed: {
        cartData() {
            return this.cart || this.internalCart;
        },
    },
    created() {
        if (!this.cart) {
            this.fetchCart();
        }
        EventBus.$on("cart-updated", (cart) => {
            if (cart) {
                this.internalCart = cart;
            }
            this.$forceUpdate();
        });
    },
    beforeDestroy() {
        EventBus.$off("cart-updated");
    },
    methods: {
        async fetchCart() {
            try {
                this.isLoading = true;
                this.internalCart = await this.api.client.invoke("readCart get /checkout/cart");
            } catch (error) {
                console.error("Error fetching cart:", error);

                // Implement retry logic
                if (this.retryCount < this.maxRetries) {
                    this.retryCount++;
                    console.log(`Retrying cart fetch (${this.retryCount}/${this.maxRetries})...`);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait before retry
                    await this.fetchCart();
                } else {
                    EventBus.$emit('trigger-alert', {
                        message: this.$t('checkout.cart_load_error'),
                        state: 'error'
                    });
                    this.retryCount = 0;
                }
            } finally {
                this.isLoading = false;
            }
        },
        debouncedUpdateQuantity: _.debounce(function (itemId, quantity) {
            this.updateQuantity(itemId, quantity);
        }, 500),
        async updateQuantity(itemId, quantity) {
            if (!Number.isInteger(parseInt(quantity)) || parseInt(quantity) < 1) {
                // Reset to previous quantity if invalid
                this.$forceUpdate();
                return;
            }

            try {
                this.updatingQuantityItems.push(itemId);
                EventBus.$emit('updating-quantity-items', true);

                let items = this.cartData.lineItems
                    .filter(item => this.canUpdateQuantity(item))
                    .map((item) => {
                        if (item.id === itemId) {
                            item.quantity = parseInt(quantity);
                        }

                        return {
                            id: item.id,
                            quantity: parseInt(item.quantity)
                        };
                    });

                const updatedCart = await this.api.client.invoke("updateLineItem patch /checkout/cart/line-item", { items });

                if (this.cart) {
                    this.$emit('update-cart', updatedCart);
                } else {
                    this.internalCart = updatedCart;
                }

                // Store transaction ID for tracking
                if (updatedCart && updatedCart.token) {
                    localStorage.setItem("transactionId", updatedCart.token);
                }

                EventBus.$emit("cart-updated", updatedCart);

                // Track the update-cart event for analytics
                this.trackUpdateCartEvent(updatedCart, itemId, parseInt(quantity));
            } catch (error) {
                console.error("Error updating quantity:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.quantity_update_error'),
                    state: 'error'
                });
                // Reset UI state
                this.$forceUpdate();
            } finally {
                this.updatingQuantityItems = this.updatingQuantityItems.filter((id) => id !== itemId);
                EventBus.$emit('updating-quantity-items', false);
            }
        },
        async removeLineItem(itemId) {
            try {
                EventBus.$emit('updating-quantity-items', true);
                const updatedCart = await this.api.client.invoke("removeLineItem delete /checkout/cart/line-item", {
                    ids: [itemId],
                });

                if (this.cart) {
                    this.$emit('update-cart', updatedCart);
                } else {
                    this.internalCart = updatedCart;
                }

                // Store transaction ID for tracking
                if (updatedCart && updatedCart.token) {
                    localStorage.setItem("transactionId", updatedCart.token);
                }

                // Track remove from cart event for analytics
                this.trackRemoveFromCartEvent(itemId);

                EventBus.$emit("cart-updated", updatedCart);

                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.item_removed'),
                    state: 'success'
                });
            } catch (error) {
                console.error("Error removing item:", error);
                EventBus.$emit('trigger-alert', {
                    message: this.$t('checkout.item_remove_error'),
                    state: 'error'
                });
            } finally {
                EventBus.$emit('updating-quantity-items', false);
            }
        },
        canUpdateQuantity(lineItem) {
            if (!lineItem) return false;

            if (lineItem.payload && 'productVoucherCodeProduct' in lineItem.payload) {
                return false;
            }

            if (lineItem.type === 'promotion') {
                return false;
            }

            return lineItem.stackable;
        },
        isUpdatingQuantity(itemId) {
            return this.updatingQuantityItems.includes(itemId);
        },
        incrementQuantity(lineItem) {
            this.updateQuantity(lineItem.id, lineItem.quantity + 1);
        },
        decrementQuantity(lineItem) {
            if (lineItem.quantity > 1) {
                this.updateQuantity(lineItem.id, lineItem.quantity - 1);
            }
        },
        getGTMItem(item, index) {
            return {
                item_id: item.payload?.productNumber || '',
                item_name: item.label,
                item_list_name: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher'),
                item_brand: item.payload?.manufacturer?.name || "DCM",
                price: item.price?.unitPrice || 0,
                quantity: item.quantity || 1,
                index: index + 1,
                currency: 'EUR',
                item_category: item.payload?.category?.name || (item.payload?.productNumber ? 'insectenbestrijders' : 'voucher')
            };
        },
        trackUpdateCartEvent(cart, itemId, newQuantity) {
            if (!cart || !cart.lineItems) return;

            const updatedItem = cart.lineItems.find(item => item.id === itemId);
            if (!updatedItem) return;

            const index = cart.lineItems.indexOf(updatedItem);
            const gtmItem = this.getGTMItem(updatedItem, index);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'update_cart_item',
                'item': gtmItem,
                'value': cart.price?.totalPrice || 0,
                'new_quantity': newQuantity
            });
        },
        trackRemoveFromCartEvent(itemId) {
            const originalItem = this.cartData.lineItems.find(item => item.id === itemId);
            if (!originalItem) return;

            const index = this.cartData.lineItems.indexOf(originalItem);
            const gtmItem = this.getGTMItem(originalItem, index);

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                'event': 'remove_from_cart',
                'item': gtmItem
            });
        }
    }
};
</script>

<style scoped>
.quantity-controls {
    display: flex;
    align-items: center;
}

.quantity-input {
    width: 50px;
    text-align: center;
    margin: 0 5px;
}

.quantity-btn {
    padding: 2px 8px;
}

.voucher-icon {
    width: 100%;
    height: 100%;
    background-color: #f8f9fa;
    border-radius: 4px;
}
</style>
